import { useCallback, useEffect, useMemo, useState } from "react";
import { Check, X, CalendarHeart, CalendarPlus, RefreshCw } from "lucide-react";
import { api, type DateItem } from "../../lib/api";
import {
  relativeDayChip,
  formatNextOccurrence,
  originYearLabel,
  DateGlyph,
  DatePersonAvatar,
  kindLabel,
  downloadIcs,
} from "../../lib/dates-ui";

/** The /dates anchor under Explore. Every date Memso has woven from
 *  any signal: birthdays, anniversaries, recurring and one-off
 *  events. Grouped by kind, each row carries the inferred-vs-confirmed
 *  treatment with one-tap Confirm / Dismiss, and an .ics export.
 *  Recurring dates surface a "since <year>" affordance when the
 *  original year is known. */
export function Dates() {
  const [dates, setDates] = useState<DateItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [gone, setGone] = useState<Set<string>>(new Set());

  const refresh = useCallback(() => {
    setError(null);
    api
      .datesAll()
      .then((d) => setDates(d.dates))
      .catch((e: Error) => setError(e.message));
  }, []);

  useEffect(refresh, [refresh]);

  const confirm = async (id: string) => {
    if (busy) return;
    setBusy(id);
    try {
      await api.dateConfirm(id);
      refresh();
    } finally {
      setBusy(null);
    }
  };

  const reject = async (id: string) => {
    if (busy) return;
    setBusy(id);
    try {
      await api.dateReject(id);
      setGone((prev) => new Set(prev).add(id));
    } finally {
      setBusy(null);
    }
  };

  const grouped = useMemo(() => {
    const live = (dates ?? []).filter((d) => !gone.has(d.date_id));
    const byKind = new Map<string, DateItem[]>();
    for (const d of live) {
      const arr = byKind.get(d.kind) ?? [];
      arr.push(d);
      byKind.set(d.kind, arr);
    }
    for (const arr of byKind.values()) {
      arr.sort((a, b) => a.days_until - b.days_until);
    }
    return [...byKind.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [dates, gone]);

  if (error) {
    return (
      <div className="rounded-lg border border-rose-300 dark:border-rose-700 bg-rose-50 dark:bg-rose-900/30 px-4 py-3 text-sm text-rose-800 dark:text-rose-200">
        Could not load your dates. {error}
      </div>
    );
  }

  if (dates === null) {
    return (
      <div className="space-y-3">
        <div className="h-24 rounded-2xl bg-slate-100 dark:bg-slate-800/40 animate-pulse" />
        <div className="h-24 rounded-2xl bg-slate-100 dark:bg-slate-800/40 animate-pulse" />
      </div>
    );
  }

  const total = grouped.reduce((n, [, arr]) => n + arr.length, 0);

  if (total === 0) {
    return (
      <section className="glass rounded-2xl border border-slate-200/70 dark:border-slate-700/50 backdrop-blur p-8 sm:p-10 text-center space-y-3">
        <div className="mx-auto inline-flex w-14 h-14 rounded-2xl bg-gradient-to-br from-pink-400 to-rose-500 items-center justify-center text-white shadow-md">
          <CalendarHeart className="w-7 h-7" strokeWidth={2.5} />
        </div>
        <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100">
          No dates woven yet
        </h2>
        <p className="text-sm text-slate-500 dark:text-slate-400 max-w-md mx-auto">
          Memso captures birthdays, anniversaries, and events from any signal you
          connect. As your sources flow in, they will surface here.
        </p>
      </section>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-slate-500 dark:text-slate-400">
          {total} {total === 1 ? "date" : "dates"} woven across {grouped.length}{" "}
          {grouped.length === 1 ? "kind" : "kinds"}.
        </p>
        <button
          type="button"
          onClick={refresh}
          aria-label="Refresh"
          className="inline-flex items-center gap-1.5 rounded-lg bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 text-xs font-semibold px-2.5 py-1.5 transition-colors"
        >
          <RefreshCw className="w-3.5 h-3.5" />
          Refresh
        </button>
      </div>

      {grouped.map(([kind, items]) => (
        <section key={kind} className="space-y-2">
          <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
            {kindLabel(kind)} ({items.length})
          </h3>
          <ul className="space-y-1.5">
            {items.map((d) => {
              const dotted = d.is_inferred && !d.confirmed;
              const since = originYearLabel(d);
              return (
                <li
                  key={d.date_id}
                  className={`rounded-xl p-3 flex flex-col sm:flex-row sm:items-center gap-3 ${
                    dotted
                      ? "border border-dashed border-amber-400/70 dark:border-amber-500/60 bg-amber-50/50 dark:bg-amber-950/20"
                      : "glass border border-slate-200/70 dark:border-slate-700/50 backdrop-blur"
                  }`}
                >
                  {d.person ? (
                    <DatePersonAvatar person={d.person} inferred={dotted} />
                  ) : (
                    <DateGlyph />
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-slate-900 dark:text-slate-100 truncate">
                        {d.person ? d.person.display_name : d.display_name}
                      </span>
                      <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full bg-pink-200 dark:bg-pink-700/40 text-pink-800 dark:text-pink-200">
                        {kindLabel(d.kind)}
                      </span>
                      <span className="text-[11px] font-semibold text-rose-600 dark:text-rose-300">
                        {relativeDayChip(d.days_until)}
                      </span>
                      {since && (
                        <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300">
                          {since}
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
                      {formatNextOccurrence(d.next_occurrence)}
                      {" · "}
                      {Math.round(d.confidence * 100)}% confidence
                      {dotted ? " · inferred" : " · confirmed"}
                    </div>
                    {d.description && (
                      <div className="text-xs text-slate-600 dark:text-slate-300 mt-0.5 line-clamp-2">
                        {d.description}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0 flex-wrap">
                    {dotted && (
                      <button
                        type="button"
                        onClick={() => confirm(d.date_id)}
                        disabled={busy === d.date_id}
                        className="inline-flex items-center gap-1 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold px-2 py-1.5 disabled:opacity-50"
                      >
                        <Check className="w-3.5 h-3.5" />
                        Confirm
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => downloadIcs(d)}
                      className="inline-flex items-center gap-1 rounded-md bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200 text-xs font-semibold px-2 py-1.5 hover:bg-slate-300 dark:hover:bg-slate-600"
                    >
                      <CalendarPlus className="w-3.5 h-3.5" />
                      Add to calendar
                    </button>
                    <button
                      type="button"
                      onClick={() => reject(d.date_id)}
                      disabled={busy === d.date_id}
                      className="inline-flex items-center gap-1 rounded-md bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 text-xs font-semibold px-2 py-1.5 hover:bg-rose-100 dark:hover:bg-rose-900/30 hover:text-rose-700 dark:hover:text-rose-300 disabled:opacity-50"
                      aria-label="Dismiss"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        </section>
      ))}
    </div>
  );
}
